import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  where, 
  orderBy, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc 
} from 'firebase/firestore';
import { db } from '../../configs/firebase';
import { EAuthData } from '../Provider/EAuthProvider';
import { 
  FaBullhorn, 
  FaPlus, 
  FaEdit, 
  FaTrash, 
  FaEye, 
  FaUsers, 
  FaCalendar,
  FaTimes,
  FaSave,
  FaSearch,
  FaGraduationCap,
  FaMapMarkerAlt,
  FaClock,
  FaCheckCircle,
  FaPauseCircle,
  FaGlobe
} from 'react-icons/fa';

const EmployerAnnouncements = () => {
  const { user } = EAuthData();
  const [announcements, setAnnouncements] = useState([]);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const countriesOptions = [
    'Ghana'
  ];

  // Form state for creating/editing announcements
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    content: '',
    type: 'general',
    targetAudience: {
      skills: [],
      interests: [],
      graduationYear: 'any',
      countries: [],
      location: 'any',
      gpa: 'any'
    },
    priority: 'medium',
    expiresAt: '',
    isPublished: false
  });

  const [stats, setStats] = useState({
    total: 0,
    published: 0,
    draft: 0,
    expired: 0
  });

  // Predefined options
  const skillsOptions = [
    'JavaScript', 'Python', 'Java', 'React', 'Node.js', 'SQL', 'HTML/CSS',
    'Data Analysis', 'Machine Learning', 'Project Management', 'Marketing',
    'Graphic Design', 'Content Writing', 'Sales', 'Customer Service'
  ];

  const interestsOptions = [
    'Software Development', 'Data Science', 'Marketing', 'Finance', 'Design',
    'Healthcare', 'Education', 'Non-profit', 'Technology', 'Business',
    'Engineering', 'Media', 'Research', 'Consulting', 'Startup'
  ];

  // Load announcements
  useEffect(() => {
    if (user?.userd?.uid) {
      fetchAnnouncements();
    }
  }, [user]);

  const fetchAnnouncements = async () => {
    try {
      setLoading(true);
      const announcementsRef = collection(db, 'announcements');
      const q = query(
        announcementsRef,
        where('employerId', '==', user.userd.uid),
        orderBy('createdAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const announcementsData = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setAnnouncements(announcementsData);
      calculateStats(announcementsData);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const now = new Date();
    const published = data.filter(a => a.isPublished).length;
    const draft = data.filter(a => !a.isPublished).length;
    const expired = data.filter(a => a.expiresAt && new Date(a.expiresAt) < now).length;

    setStats({
      total: data.length,
      published,
      draft,
      expired
    });
  };

  const resetForm = () => {
    setAnnouncementForm({
      title: '',
      content: '',
      type: 'general',
      targetAudience: {
        skills: [],
        interests: [],
        graduationYear: 'any',
        countries: [],
        location: 'any',
        gpa: 'any'
      },
      priority: 'medium',
      expiresAt: '',
      isPublished: false
    });
  };

  const validateAnnouncementForm = () => {
    const errors = [];
    
    if (!announcementForm.title.trim()) {
      errors.push('Title is required');
    } else if (announcementForm.title.trim().length < 5) {
      errors.push('Title must be at least 5 characters long');
    } else if (announcementForm.title.trim().length > 100) {
      errors.push('Title must be less than 100 characters');
    }
    
    if (!announcementForm.content.trim()) {
      errors.push('Content is required');
    } else if (announcementForm.content.trim().length < 20) {
      errors.push('Content must be at least 20 characters long');
    } else if (announcementForm.content.trim().length > 2000) {
      errors.push('Content must be less than 2000 characters');
    }
    
    // Validate expiration date
    if (announcementForm.expiresAt) {
      const expirationDate = new Date(announcementForm.expiresAt);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (expirationDate < today) {
        errors.push('Expiration date cannot be in the past');
      }
    }
    
    return errors;
  };

  const handleCreateAnnouncement = async () => {
    const validationErrors = validateAnnouncementForm();
  
    if (validationErrors.length > 0) {
      alert('Please fix the following errors:\n\n' + validationErrors.join('\n'));
      return;
    }

    try {
      setSubmitting(true);
      const newAnnouncement = {
        ...announcementForm,
        employerId: user.userd.uid,
        employerName: user.userd.displayName || 'Employer',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };

      await addDoc(collection(db, 'announcements'), newAnnouncement);
      await fetchAnnouncements();
      setShowCreateModal(false);
      resetForm();
      alert('Announcement created successfully!');
    } catch (error) {
      console.error('Error creating announcement:', error);
      alert('Error creating announcement. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditAnnouncement = async () => {
    if (!announcementForm.title.trim() || !announcementForm.content.trim()) {
      alert('Please fill in title and content');
      return;
    }

    try {
      setSubmitting(true);
      const announcementRef = doc(db, 'announcements', selectedAnnouncement.id);
      await updateDoc(announcementRef, {
        ...announcementForm,
        updatedAt: new Date().toISOString()
      });

      await fetchAnnouncements();
      setShowEditModal(false);
      setSelectedAnnouncement(null);
      resetForm();
      alert('Announcement updated successfully!');
    } catch (error) {
      console.error('Error updating announcement:', error);
      alert('Error updating announcement. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (window.confirm('Are you sure you want to delete this announcement?')) {
      try {
        await deleteDoc(doc(db, 'announcements', id));
        await fetchAnnouncements();
        alert('Announcement deleted successfully!');
      } catch (error) {
        console.error('Error deleting announcement:', error);
        alert('Error deleting announcement. Please try again.');
      }
    }
  };

  const openEditModal = (announcement) => {
    setSelectedAnnouncement(announcement);
    setAnnouncementForm({
      title: announcement.title,
      content: announcement.content,
      type: announcement.type,
      targetAudience: announcement.targetAudience || {
        skills: [],
        interests: [],
        graduationYear: 'any',
        countries: [],
        location: 'any',
        gpa: 'any'
      },
      priority: announcement.priority,
      expiresAt: announcement.expiresAt ? announcement.expiresAt.split('T')[0] : '',
      isPublished: announcement.isPublished
    });
    setShowEditModal(true);
  };

  const togglePublishStatus = async (id, currentStatus) => {
    try {
      const announcementRef = doc(db, 'announcements', id);
      await updateDoc(announcementRef, {
        isPublished: !currentStatus,
        updatedAt: new Date().toISOString()
      });
      await fetchAnnouncements();
    } catch (error) {
      console.error('Error updating publish status:', error);
      alert('Error updating status. Please try again.');
    }
  };

  const filteredAnnouncements = announcements.filter(announcement => {
    const matchesSearch = announcement.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         announcement.content.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesFilter = filterStatus === 'all' || 
                         (filterStatus === 'published' && announcement.isPublished) ||
                         (filterStatus === 'draft' && !announcement.isPublished) ||
                         (filterStatus === 'expired' && announcement.expiresAt && new Date(announcement.expiresAt) < new Date());
    
    return matchesSearch && matchesFilter;
  });

  const getTypeColor = (type) => {
    switch (type) {
      case 'job_posting': return 'bg-blue-100 text-blue-800';
      case 'event': return 'bg-green-100 text-green-800';
      case 'deadline': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Enhanced target audience display
  const renderTargetAudienceDisplay = (announcement) => {
    const { targetAudience } = announcement;
    if (!targetAudience) return null;

    const hasTargeting = (
      (targetAudience.skills?.length > 0) ||
      (targetAudience.interests?.length > 0) ||
      (targetAudience.countries?.length > 0) ||
      (targetAudience.graduationYear && targetAudience.graduationYear !== 'any') ||
      (targetAudience.location && targetAudience.location !== 'any') ||
      (targetAudience.gpa && targetAudience.gpa !== 'any')
    );

    if (!hasTargeting) {
      return (
        <div className="mt-3 p-3 bg-green-50 rounded-lg">
          <div className="flex items-center">
            <FaGlobe className="text-green-600 mr-2" />
            <h4 className="text-sm font-medium text-green-700">Global Audience</h4>
          </div>
          <div className="text-sm text-green-600 mt-1">No specific targeting requirements</div>
        </div>
      );
    }

    return (
      <div className="mt-3 p-3 bg-blue-50 rounded-lg">
        <div className="flex items-center mb-2">
          <FaUsers className="text-blue-600 mr-2" />
          <h4 className="text-sm font-medium text-blue-700">Targeted Audience</h4>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-blue-600">
          {targetAudience.skills?.length > 0 && (
            <div className="flex items-start">
              <span className="font-medium mr-1">Skills:</span>
              <span className="flex-1">{targetAudience.skills.join(', ')}</span>
            </div>
          )}
          {targetAudience.interests?.length > 0 && (
            <div className="flex items-start">
              <span className="font-medium mr-1">Interests:</span>
              <span className="flex-1">{targetAudience.interests.join(', ')}</span>
            </div>
          )}
          {targetAudience.countries?.length > 0 && (
            <div className="flex items-start">
              <FaMapMarkerAlt className="text-blue-500 mr-1 mt-0.5 flex-shrink-0" />
              <span className="flex-1">{targetAudience.countries.join(', ')}</span>
            </div>
          )}
          {targetAudience.graduationYear && targetAudience.graduationYear !== 'any' && (
            <div className="flex items-start">
              <FaGraduationCap className="text-blue-500 mr-1 mt-0.5 flex-shrink-0" />
              <span className="flex-1">Class of {targetAudience.graduationYear}</span>
            </div>
          )}
          {targetAudience.location && targetAudience.location !== 'any' && (
            <div className="flex items-start">
              <span className="font-medium mr-1">Location:</span>
              <span className="flex-1">{targetAudience.location}</span>
            </div>
          )}
          {targetAudience.gpa && targetAudience.gpa !== 'any' && (
            <div className="flex items-start">
              <span className="font-medium mr-1">Min GPA:</span>
              <span className="flex-1">{targetAudience.gpa}+</span>
            </div>
          )}
        </div>
      </div>
    );
  };

  const CharacterCounter = ({ current, max, className = "" }) => {
    const percentage = (current / max) * 100;
    const isNearLimit = percentage > 80;
    const isAtLimit = percentage > 100;
    
    return (
      <div className={`text-sm ${isAtLimit ? 'text-red-500' : isNearLimit ? 'text-yellow-600' : 'text-gray-500'} ${className}`}>
        {current}/{max} characters
        {isAtLimit && <span className="ml-2 font-medium">Character limit exceeded!</span>}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-orange-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading announcements...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">Announcements</h1>
            <p className="text-gray-600">Create and manage announcements for students</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="mt-4 md:mt-0 bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center"
          >
            <FaPlus className="mr-2" />
            Create Announcement
          </button>
        </div>

        {/* Simplified Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6 mb-8">
          <div className="bg-white p-4 md:p-6 rounded-lg shadow-md border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xs md:text-sm font-medium text-gray-600">Total</h3>
                <p className="text-xl md:text-2xl font-bold text-indigo-600">{stats.total}</p>
              </div>
              <div className="bg-indigo-100 p-2 md:p-3 rounded-full">
                <FaBullhorn className="h-4 md:h-6 w-4 md:w-6 text-indigo-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-4 md:p-6 rounded-lg shadow-md border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xs md:text-sm font-medium text-gray-600">Published</h3>
                <p className="text-xl md:text-2xl font-bold text-green-600">{stats.published}</p>
              </div>
              <div className="bg-green-100 p-2 md:p-3 rounded-full">
                <FaCheckCircle className="h-4 md:h-6 w-4 md:w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-4 md:p-6 rounded-lg shadow-md border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xs md:text-sm font-medium text-gray-600">Draft</h3>
                <p className="text-xl md:text-2xl font-bold text-yellow-600">{stats.draft}</p>
              </div>
              <div className="bg-yellow-100 p-2 md:p-3 rounded-full">
                <FaPauseCircle className="h-4 md:h-6 w-4 md:w-6 text-yellow-600" />
              </div>
            </div>
          </div>

          <div className="bg-white p-4 md:p-6 rounded-lg shadow-md border border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-xs md:text-sm font-medium text-gray-600">Expired</h3>
                <p className="text-xl md:text-2xl font-bold text-red-600">{stats.expired}</p>
              </div>
              <div className="bg-red-100 p-2 md:p-3 rounded-full">
                <FaClock className="h-4 md:h-6 w-4 md:w-6 text-red-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="bg-white p-4 md:p-6 rounded-lg shadow-md border border-gray-200 mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
              <div className="relative">
                <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search announcements..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500 w-full sm:w-auto"
                />
              </div>
              
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">All Status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>
        </div>

        {/* Announcements List */}
        <div className="space-y-6">
          {filteredAnnouncements.map(announcement => (
            <div key={announcement.id} className="bg-white rounded-lg shadow-md border border-gray-200 p-4 md:p-6">
              <div className="flex flex-col lg:flex-row justify-between items-start mb-4">
                <div className="flex-1 mb-4 lg:mb-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <h3 className="text-lg md:text-xl font-semibold text-gray-800">{announcement.title}</h3>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(announcement.type)}`}>
                      {announcement.type.replace('_', ' ').toUpperCase()}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(announcement.priority)}`}>
                      {announcement.priority.toUpperCase()}
                    </span>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      announcement.isPublished ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {announcement.isPublished ? 'PUBLISHED' : 'DRAFT'}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 mb-3 line-clamp-2">{announcement.content}</p>
                  
                  <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                    <div className="flex items-center">
                      <FaCalendar className="mr-1" />
                      {new Date(announcement.createdAt).toLocaleDateString()}
                    </div>
                    {announcement.expiresAt && (
                      <div className="flex items-center">
                        <FaClock className="mr-1" />
                        Expires: {new Date(announcement.expiresAt).toLocaleDateString()}
                      </div>
                    )}
                  </div>

                  {/* Target Audience Summary */}
                  {renderTargetAudienceDisplay(announcement)}
                </div>

                {/* Action Buttons */}
                <div className="flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => togglePublishStatus(announcement.id, announcement.isPublished)}
                    className={`px-3 py-1 rounded text-sm font-medium ${
                      announcement.isPublished
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {announcement.isPublished ? 'Unpublish' : 'Publish'}
                  </button>
                  <button
                    onClick={() => openEditModal(announcement)}
                    className="p-2 text-blue-600 hover:bg-blue-100 rounded"
                  >
                    <FaEdit />
                  </button>
                  <button
                    onClick={() => handleDeleteAnnouncement(announcement.id)}
                    className="p-2 text-red-600 hover:bg-red-100 rounded"
                  >
                    <FaTrash />
                  </button>
                </div>
              </div>
            </div>
          ))}

          {filteredAnnouncements.length === 0 && (
            <div className="text-center py-12">
              <FaBullhorn className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No announcements found</h3>
              <p className="text-gray-600 mb-4">
                {searchTerm || filterStatus !== 'all' 
                  ? 'Try adjusting your search or filter criteria'
                  : 'Create your first announcement to get started'
                }
              </p>
              {!searchTerm && filterStatus === 'all' && (
                <button
                  onClick={() => setShowCreateModal(true)}
                  className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-2 rounded-md font-medium"
                >
                  Create Announcement
                </button>
              )}
            </div>
          )}
        </div>

        {/* Create/Edit Announcement Modal */}
        {(showCreateModal || showEditModal) && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-800">
                    {showCreateModal ? 'Create Announcement' : 'Edit Announcement'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      setShowEditModal(false);
                      setSelectedAnnouncement(null);
                      resetForm();
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <FaTimes className="text-xl" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Basic Information */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
                      <input
                        type="text"
                        value={announcementForm.title}
                        onChange={(e) => setAnnouncementForm({...announcementForm, title: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="Enter announcement title..."
                        maxLength={100}
                      />
                      <div className="flex justify-between items-center mt-1">
                        <CharacterCounter current={announcementForm.title.length} max={100} />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
                      <select
                        value={announcementForm.type}
                        onChange={(e) => setAnnouncementForm({...announcementForm, type: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="general">General</option>
                        <option value="job_posting">Job Posting</option>
                        <option value="event">Event</option>
                        <option value="deadline">Deadline</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Priority</label>
                      <select
                        value={announcementForm.priority}
                        onChange={(e) => setAnnouncementForm({...announcementForm, priority: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      >
                        <option value="low">Low</option>
                        <option value="medium">Medium</option>
                        <option value="high">High</option>
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Expiration Date</label>
                      <input
                        type="date"
                        value={announcementForm.expiresAt}
                        onChange={(e) => setAnnouncementForm({...announcementForm, expiresAt: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                      />
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Content *</label>
                      <textarea
                        value={announcementForm.content}
                        onChange={(e) => setAnnouncementForm({...announcementForm, content: e.target.value})}
                        rows="6"
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        placeholder="Enter announcement content..."
                        maxLength={2000}
                      />
                      <div className="flex justify-between items-center mt-1">
                        <CharacterCounter current={announcementForm.content.length} max={2000} />
                      </div>
                    </div>
                  </div>

                  {/* Enhanced Target Audience */}
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Target Audience</h3>
                    <p className="text-sm text-gray-600 mb-4">
                      Select criteria to target specific students. Leave as "Any" to include all students.
                    </p>
                    
                    <div className="space-y-6">
                      {/* Skills */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-medium text-gray-700">Required Skills</label>
                          <button
                            type="button"
                            onClick={() => setAnnouncementForm({
                              ...announcementForm,
                              targetAudience: { ...announcementForm.targetAudience, skills: [] }
                            })}
                            className="text-sm text-orange-600 hover:text-orange-700"
                          >
                            Clear All
                          </button>
                        </div>
                        
                        {announcementForm.targetAudience.skills.length === 0 && (
                          <div className="text-sm text-gray-500 italic mb-2">Any skills (no specific requirements)</div>
                        )}
                        
                        <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                          {skillsOptions.map(skill => (
                            <label key={skill} className="flex items-center mb-1">
                              <input
                                type="checkbox"
                                checked={announcementForm.targetAudience.skills.includes(skill)}
                                onChange={(e) => {
                                  const skills = e.target.checked
                                    ? [...announcementForm.targetAudience.skills, skill]
                                    : announcementForm.targetAudience.skills.filter(s => s !== skill);
                                  setAnnouncementForm({
                                    ...announcementForm,
                                    targetAudience: { ...announcementForm.targetAudience, skills }
                                  });
                                }}
                                className="mr-2"
                              />
                              <span className="text-sm">{skill}</span>
                            </label>
                          ))}
                          </div>
                      </div>

                      {/* Interests */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-medium text-gray-700">Interests</label>
                          <button
                            type="button"
                            onClick={() => setAnnouncementForm({
                              ...announcementForm,
                              targetAudience: { ...announcementForm.targetAudience, interests: [] }
                            })}
                            className="text-sm text-orange-600 hover:text-orange-700"
                          >
                            Clear All
                          </button>
                        </div>
                        
                        {announcementForm.targetAudience.interests.length === 0 && (
                          <div className="text-sm text-gray-500 italic mb-2">Any interests (no specific requirements)</div>
                        )}
                        
                        <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                          {interestsOptions.map(interest => (
                            <label key={interest} className="flex items-center mb-1">
                              <input
                                type="checkbox"
                                checked={announcementForm.targetAudience.interests.includes(interest)}
                                onChange={(e) => {
                                  const interests = e.target.checked
                                    ? [...announcementForm.targetAudience.interests, interest]
                                    : announcementForm.targetAudience.interests.filter(i => i !== interest);
                                  setAnnouncementForm({
                                    ...announcementForm,
                                    targetAudience: { ...announcementForm.targetAudience, interests }
                                  });
                                }}
                                className="mr-2"
                              />
                              <span className="text-sm">{interest}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Grid for other fields */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Graduation Year */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Graduation Year</label>
                          <select
                            value={announcementForm.targetAudience.graduationYear}
                            onChange={(e) => setAnnouncementForm({
                              ...announcementForm,
                              targetAudience: { ...announcementForm.targetAudience, graduationYear: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                          >
                            <option value="any">Any Year</option>
                            <option value="2025">2025</option>
                            <option value="2026">2026</option>
                            <option value="2027">2027</option>
                            <option value="2028">2028</option>
                            <option value="2029">2029</option>
                            <option value="2030">2030</option>
                          </select>
                        </div>

                        {/* GPA Requirement */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">Minimum GPA</label>
                          <select
                            value={announcementForm.targetAudience.gpa}
                            onChange={(e) => setAnnouncementForm({
                              ...announcementForm,
                              targetAudience: { ...announcementForm.targetAudience, gpa: e.target.value }
                            })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                          >
                            <option value="any">Any GPA</option>
                            <option value="1.0">1.0+</option>
                            <option value="2.0">2.0+</option>
                            <option value="3.0">3.0+</option>
                          </select>
                        </div>
                      </div>

                      {/* Countries */}
                      <div>
                        <div className="flex items-center justify-between mb-2">
                          <label className="block text-sm font-medium text-gray-700">Target Countries</label>
                          <button
                            type="button"
                            onClick={() => setAnnouncementForm({
                              ...announcementForm,
                              targetAudience: { ...announcementForm.targetAudience, countries: [] }
                            })}
                            className="text-sm text-orange-600 hover:text-orange-700"
                          >
                            Clear All
                          </button>
                        </div>
                        
                        {announcementForm.targetAudience.countries.length === 0 && (
                          <div className="text-sm text-gray-500 italic mb-2">Any country (global audience)</div>
                        )}
                        
                        <div className="max-h-32 overflow-y-auto border border-gray-300 rounded-md p-2">
                          {countriesOptions.map(country => (
                            <label key={country} className="flex items-center mb-1">
                              <input
                                type="checkbox"
                                checked={announcementForm.targetAudience.countries.includes(country)}
                                onChange={(e) => {
                                  const countries = e.target.checked
                                    ? [...announcementForm.targetAudience.countries, country]
                                    : announcementForm.targetAudience.countries.filter(c => c !== country);
                                  setAnnouncementForm({
                                    ...announcementForm,
                                    targetAudience: { ...announcementForm.targetAudience, countries }
                                  });
                                }}
                                className="mr-2"
                              />
                              <span className="text-sm">{country}</span>
                            </label>
                          ))}
                        </div>
                      </div>

                      {/* Location (City/Region) */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Specific Location (City/Region)</label>
                        <input
                          type="text"
                          value={announcementForm.targetAudience.location === 'any' ? '' : announcementForm.targetAudience.location}
                          onChange={(e) => setAnnouncementForm({
                            ...announcementForm,
                            targetAudience: { 
                              ...announcementForm.targetAudience, 
                              location: e.target.value.trim() === '' ? 'any' : e.target.value 
                            }
                          })}
                          placeholder="Any location (leave blank for any location)"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Optional: Specify city, state, or region (e.g., "San Francisco", "California", "Remote")
                        </p>
                      </div>
                    </div>

                    {/* Target Audience Summary */}
                    <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                      <h4 className="text-sm font-medium text-blue-900 mb-2">Audience Summary:</h4>
                      <div className="text-sm text-blue-800 space-y-1">
                        <div>
                          <strong>Skills:</strong> {
                            announcementForm.targetAudience.skills.length === 0 
                              ? 'Any skills' 
                              : announcementForm.targetAudience.skills.join(', ')
                          }
                        </div>
                        <div>
                          <strong>Interests:</strong> {
                            announcementForm.targetAudience.interests.length === 0 
                              ? 'Any interests' 
                              : announcementForm.targetAudience.interests.join(', ')
                          }
                        </div>
                        <div>
                          <strong>Graduation:</strong> {
                            announcementForm.targetAudience.graduationYear === 'any' 
                              ? 'Any year' 
                              : `Class of ${announcementForm.targetAudience.graduationYear}`
                          }
                        </div>
                        <div>
                          <strong>Countries:</strong> {
                            announcementForm.targetAudience.countries.length === 0 
                              ? 'Global (any country)' 
                              : announcementForm.targetAudience.countries.join(', ')
                          }
                        </div>
                        <div>
                          <strong>Location:</strong> {
                            announcementForm.targetAudience.location === 'any' 
                              ? 'Any location' 
                              : announcementForm.targetAudience.location
                          }
                        </div>
                        <div>
                          <strong>GPA:</strong> {
                            announcementForm.targetAudience.gpa === 'any' 
                              ? 'Any GPA' 
                              : `${announcementForm.targetAudience.gpa}+`
                          }
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Settings */}
                  <div className="border-t pt-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Settings</h3>
                    
                    <div className="space-y-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={announcementForm.isPublished}
                          onChange={(e) => setAnnouncementForm({...announcementForm, isPublished: e.target.checked})}
                          className="mr-3"
                        />
                        <span className="text-sm font-medium text-gray-700">Publish immediately</span>
                      </label>
                    </div>
                  </div>
                </div>

                {/* Form Actions */}
                <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-4 mt-8 pt-6 border-t">
                  <button
                    onClick={() => {
                      setShowCreateModal(false);
                      setShowEditModal(false);
                      setSelectedAnnouncement(null);
                      resetForm();
                    }}
                    className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={showCreateModal ? handleCreateAnnouncement : handleEditAnnouncement}
                    disabled={!announcementForm.title.trim() || !announcementForm.content.trim() || submitting}
                    className="px-6 py-2 bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    <FaSave className="mr-2" />
                    {submitting ? 'Saving...' : (showCreateModal ? 'Create Announcement' : 'Save Changes')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployerAnnouncements;